import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { mergeSito, SectionEditor, EditButton, HeroImageControl } from "@/components/SitoEditor";
import { ModuliList, fetchModuli } from "@/components/SitoModuli";
import { useNotizie, NotiziaCard, NotiziaMeta, notiziaImg, useNotiziaActions, NewNotiziaButton } from "@/components/SitoNotizie";
import { Link as RouterLink, useParams, useNavigate, Navigate } from "react-router-dom";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { SezioniPersonalizzate, PagineManager, usePagine } from "@/components/SitoCustom";
import { PawPrint, Trees, Binoculars, GraduationCap, ShieldCheck, Siren, Mail, MapPin, Landmark, Lock, UserPlus, Handshake, Heart, Newspaper, Presentation, ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, X, Pencil, Images } from "lucide-react";

const ICONS = [PawPrint, Trees, Binoculars, GraduationCap, ShieldCheck, Siren];
const FOTO = ["/sito/foto-1.jpg", "/sito/foto-3.jpg", "/sito/foto-4.jpg", "/sito/foto-2.jpg"];
const NAV = [["chi-siamo", "Chi siamo"], ["corsi", "Corsi"], ["notizie", "Notizie"], ["modulistica", "Modulistica"], ["galleria", "Galleria"], ["contatti", "Contatti"]];

const useSito = () => {
  const [raw, setRaw] = useState({});
  const load = useCallback(() => { api.get("/public/sito").then((r) => { setRaw(r.data || {}); }).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  return { c: mergeSito(raw), raw, reload: load };
};

const useEditMode = () => {
  const { user } = useAuth();
  const canEdit = ["admin", "coordinatore"].includes(user?.role);
  const [edit, setEditState] = useState(() => sessionStorage.getItem("sito-edit") === "1");
  const setEdit = (v) => { sessionStorage.setItem("sito-edit", v ? "1" : "0"); setEditState(v); };
  return { canEdit, edit: canEdit && edit, setEdit };
};

const EditToggle = ({ canEdit, edit, setEdit }) => (canEdit ? (
  <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-2">
    {edit && <PagineManager />}
    <button type="button" onClick={() => setEdit(!edit)} data-testid="sito-edit-toggle"
      className={`inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold shadow-2xl transition-colors ${edit ? "bg-[#D97706] text-white" : "bg-[#14382C] text-white hover:bg-[#0E281F]"}`}>
      <Pencil className="h-4 w-4" /> {edit ? "Esci dalla modifica" : "Modifica sito"}
    </button>
  </div>
) : null);

const SectionTitle = ({ kicker, title }) => (
  <div className="mb-10">
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97706]">{kicker}</p>
    <h2 className="mt-2 font-heading text-3xl font-extrabold text-[#14382C] sm:text-4xl">{title}</h2>
  </div>
);

const navTo = (id) => (id === "galleria" ? "/sito/galleria" : id === "notizie" ? "/sito/notizie" : `/sito#${id}`);
const PageLink = ({ p, prefix, className }) => <Link to={`/sito/p/${p.slug}`} data-testid={`${prefix}-p-${p.slug}`} className={className}>{p.titolo}</Link>;

const AltroMenu = ({ pagine, prefix, className }) => (
  <DropdownMenu>
    <DropdownMenuTrigger data-testid={`${prefix}-altro`} className={`inline-flex items-center gap-1 ${className}`}>Altro <ChevronDown className="h-3.5 w-3.5" /></DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      {pagine.map((p) => <DropdownMenuItem key={p.id} asChild><PageLink p={p} prefix={prefix} className="cursor-pointer" /></DropdownMenuItem>)}
    </DropdownMenuContent>
  </DropdownMenu>
);

const NavLinks = ({ prefix, className, pagine, max = 99 }) => (
  <>
    {NAV.map(([id, l]) => <Link key={id} to={navTo(id)} data-testid={`${prefix}-${id}`} className={className}>{l}</Link>)}
    {pagine.slice(0, max).map((p) => <PageLink key={p.id} p={p} prefix={prefix} className={className} />)}
    {pagine.length > max && <AltroMenu pagine={pagine.slice(max)} prefix={prefix} className={className} />}
  </>
);

const Header = () => {
  const { pagine } = usePagine();
  return (
  <header className="sticky top-0 z-30 border-b border-white/10 bg-[#14382C]/95 backdrop-blur-md">
    <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3">
      <Link to="/sito" className="flex items-center gap-3" data-testid="sito-logo">
        <img src="/emblem.png" alt="Emblema A.N.G.E." className="h-11 w-auto" />
        <span className="hidden whitespace-nowrap font-heading text-sm font-extrabold leading-tight text-white sm:block md:hidden xl:block">A.N.G.E.<br /><span className="font-medium text-emerald-100/80">Guardie Ecozoofile ODV</span></span>
      </Link>
      <nav className="hidden items-center gap-x-4 md:flex lg:gap-x-6">
        <NavLinks pagine={pagine} max={1} prefix="sito-nav" className="whitespace-nowrap text-sm font-medium text-emerald-50/90 transition-colors hover:text-[#FBBF24]" />
      </nav>
      <Link to="/login" data-testid="sito-area-riservata" className="inline-flex shrink-0 whitespace-nowrap items-center gap-2 rounded-full bg-[#D97706] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#b45309]"><Lock className="h-3.5 w-3.5" /> Area riservata</Link>
    </div>
    <nav className="flex gap-5 overflow-x-auto border-t border-white/10 px-5 py-2 md:hidden">
      <NavLinks pagine={pagine} prefix="sito-nav-mobile" className="whitespace-nowrap text-xs font-medium text-emerald-50/90" />
    </nav>
  </header>
  );
};

const Hero = ({ d, imgV, edit, onEdit }) => (
  <section id="top" className="relative overflow-hidden bg-[#14382C]">
    <img src={imgV ? `${API}/public/sito/hero-image?v=${encodeURIComponent(imgV)}` : "/sito/foto-1.jpg"} alt="Guardie Ecozoofile A.N.G.E. in servizio" data-testid="sito-hero-image" className="absolute inset-0 h-full w-full object-cover opacity-35" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#14382C] via-[#14382C]/85 to-transparent" />
    <EditButton section="hero" edit={edit} onClick={onEdit} dark />
    <div className="relative mx-auto max-w-6xl px-5 py-24 sm:py-32 gz-fade-up">
      <p data-testid="sito-hero-kicker" className="text-xs font-bold uppercase tracking-[0.25em] text-[#FBBF24]">{d.kicker}</p>
      <h1 data-testid="sito-hero-title" className="mt-4 max-w-3xl font-heading text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl">{d.title}</h1>
      <p data-testid="sito-hero-subtitle" className="mt-6 max-w-xl whitespace-pre-line text-base text-emerald-50/85 md:text-lg">{d.subtitle}</p>
      <div className="mt-10 flex flex-wrap gap-3">
        {d.join_url && <a href={d.join_url} target="_blank" rel="noopener noreferrer" data-testid="sito-hero-iscriviti" className="inline-flex items-center gap-2 rounded-full bg-[#D97706] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"><UserPlus className="h-4 w-4" /> Iscriviti all'associazione</a>}
        <a href="#corsi" data-testid="sito-hero-corsi" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"><GraduationCap className="h-4 w-4" /> Scopri i corsi</a>
      </div>
    </div>
  </section>
);

const ChiSiamo = ({ d, edit, onEdit }) => (
  <section id="chi-siamo" className="relative mx-auto max-w-6xl px-5 py-20">
    <EditButton section="chi" edit={edit} onClick={onEdit} />
    <SectionTitle kicker={d.kicker} title={d.title} />
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {(d.items || []).map(({ t, d: desc }, i) => {
        const Icon = ICONS[i % ICONS.length];
        return (
          <div key={i} data-testid={`sito-mission-${i}`} className="group rounded-xl border border-slate-200 bg-white p-6 transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-lg">
            <Icon className="h-7 w-7 text-[#166534] transition-colors group-hover:text-[#D97706]" />
            <p className="mt-4 font-heading text-lg font-bold text-slate-900">{t}</p>
            <p className="mt-1 text-sm text-slate-600">{desc}</p>
          </div>
        );
      })}
    </div>
  </section>
);

const Corsi = ({ d, pdf, edit, onEdit }) => (
  <section id="corsi" className="relative bg-[#14382C] py-20 text-white">
    <EditButton section="corsi" edit={edit} onClick={onEdit} dark />
    <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-2">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FBBF24]">{d.kicker}</p>
        <h2 className="mt-2 font-heading text-3xl font-extrabold sm:text-4xl">{d.title}</h2>
        <p data-testid="sito-corsi-text" className="mt-4 whitespace-pre-line text-emerald-50/80">{d.text}</p>
        {pdf && (
          <a href={`${API}/public/presentazione/file`} target="_blank" rel="noopener noreferrer" data-testid="sito-presentazione-link" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#14382C] transition-transform hover:-translate-y-0.5"><Presentation className="h-4 w-4" /> Presentazione Corso di Formazione d'ingresso</a>
        )}
      </div>
      <ol className="space-y-3">
        {(d.items || []).map((c, i) => (
          <li key={i} data-testid={`sito-corso-${i}`} className="flex items-center gap-4 rounded-lg border border-white/15 bg-white/5 p-4">
            <span className="font-heading text-2xl font-extrabold text-[#FBBF24]">{String(i + 1).padStart(2, "0")}</span>
            <span className="text-sm font-medium sm:text-base">{c}</span>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const Modulistica = ({ d, email, edit, onEdit }) => {
  const [moduli, setModuli] = useState([]);
  const refresh = useCallback(() => { fetchModuli().then(setModuli).catch(() => {}); }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return (
    <section id="modulistica" className="relative mx-auto max-w-6xl px-5 py-20">
      <EditButton section="moduli" edit={edit} onClick={onEdit} />
      <SectionTitle kicker={d.kicker} title={d.title} />
      <p className="-mt-6 mb-8 max-w-2xl whitespace-pre-line text-slate-600">{d.intro} {email && <a href={`mailto:${email}`} className="font-semibold text-[#166534] hover:underline">{email}</a>}</p>
      <ModuliList moduli={moduli} edit={edit} refresh={refresh} />
    </section>
  );
};

const Lightbox = ({ items, index, setIndex }) => {
  const n = items.length;
  const go = (d) => setIndex((v) => (v + d + n) % n);
  const touchX = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setIndex(null); }
      if (e.key === "ArrowLeft") { go(-1); }
      if (e.key === "ArrowRight") { go(1); }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  });
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - (touchX.current ?? 0);
    if (touchX.current !== null && Math.abs(dx) > 50) { go(dx < 0 ? 1 : -1); }
    touchX.current = null;
  };
  const { src, cap } = items[index];
  const btn = "absolute top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white backdrop-blur-md transition-colors hover:bg-white/25";
  return (
    <div data-testid="lightbox" className="fixed inset-0 z-50 flex items-center justify-center bg-black/95" onClick={() => setIndex(null)} onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd}>
      <img src={src} alt={cap || `Foto ${index + 1}`} data-testid="lightbox-image" onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-[92vw] rounded-lg object-contain shadow-2xl" />
      <button type="button" aria-label="Chiudi" data-testid="lightbox-close" onClick={() => setIndex(null)} className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/25"><X className="h-6 w-6" /></button>
      {n > 1 && (
        <>
          <button type="button" aria-label="Precedente" data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); go(-1); }} className={`${btn} left-3 sm:left-6`}><ChevronLeft className="h-7 w-7" /></button>
          <button type="button" aria-label="Successiva" data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); go(1); }} className={`${btn} right-3 sm:right-6`}><ChevronRight className="h-7 w-7" /></button>
        </>
      )}
      <div className="absolute inset-x-0 bottom-4 px-6 text-center text-white">
        {cap && <p data-testid="lightbox-caption" className="text-sm">{cap}</p>}
        <p data-testid="lightbox-counter" className="mt-1 text-xs text-white/60">{index + 1} / {n}</p>
      </div>
    </div>
  );
};

const Galleria = ({ d, edit, onEdit }) => {
  const [dbFoto, setDbFoto] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/public/galleria").then((r) => { setDbFoto(r.data || []); }).catch(() => {});
  }, []);
  const items = dbFoto.length ? dbFoto.map((f) => ({ src: `${API}/public/galleria/${f.id}/image`, cap: f.didascalia })) : FOTO.map((src) => ({ src, cap: "" }));
  return (
    <section id="galleria" className="relative min-h-[70vh] bg-slate-100 py-16">
      <EditButton section="galleria" edit={edit} onClick={onEdit} />
      <div className="mx-auto max-w-6xl px-5">
        <Link to="/sito" data-testid="sito-galleria-back" className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-[#166534] hover:underline"><ArrowLeft className="h-4 w-4" /> Torna al sito</Link>
        <SectionTitle kicker={d.kicker} title={d.title} />
        {edit && <Link to="/galleria" data-testid="sito-galleria-manage" className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#D97706] px-4 py-2 text-xs font-semibold text-white"><Images className="h-4 w-4" /> Gestisci le foto</Link>}
        <div className="grid gap-4 md:grid-cols-3">
          {items.map(({ src, cap }, i) => (
            <figure key={src} className={`relative overflow-hidden rounded-xl ${dbFoto.length === 0 && i === 0 ? "md:col-span-2 md:row-span-2" : ""} ${dbFoto.length === 0 && i === 3 ? "md:col-span-3" : ""}`}>
              <img src={src} alt={cap || `Attività A.N.G.E. ${i + 1}`} data-testid={`sito-foto-${i}`} loading="lazy" onClick={() => setOpen(i)} className={`h-full w-full cursor-zoom-in object-cover transition-transform duration-500 hover:scale-105 ${dbFoto.length ? "aspect-[4/3]" : ""}`} />
              {cap && <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8 text-sm text-white">{cap}</figcaption>}
            </figure>
          ))}
        </div>
        <Link to="/sito/notizie" data-testid="sito-notizie-link" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#166534] hover:underline"><Newspaper className="h-4 w-4" /> Notizie ed eventi A.N.G.E.</Link>
      </div>
      {open !== null && <Lightbox items={items} index={open} setIndex={setOpen} />}
    </section>
  );
};

const UltimeNotizie = ({ edit }) => {
  const { items, reload } = useNotizie(3);
  const act = useNotiziaActions(reload);
  if (!items || (!items.length && !edit)) { return null; }
  return (
    <section id="notizie" className="relative bg-slate-100 py-20" data-testid="sito-ultime-notizie">
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionTitle kicker="Notizie ed eventi" title="Dall'Associazione" />
          <div className="mb-10 flex gap-3">
            {edit && <NewNotiziaButton onClick={act.openNew} />}
            <Link to="/sito/notizie" data-testid="sito-notizie-all" className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-medium text-slate-700 hover:border-[#166534] hover:text-[#166534]">Tutte le notizie</Link>
          </div>
        </div>
        {items.length === 0 ? <p className="text-sm text-slate-500" data-testid="notizie-empty">Nessuna notizia pubblicata. I visitatori non vedono questa sezione finché non pubblichi la prima.</p> : (
          <div className="grid gap-6 md:grid-cols-3">
            {items.map((n, i) => <NotiziaCard key={n.id} n={n} i={i} edit={edit} onEdit={act.openEdit} onDelete={act.remove} />)}
          </div>
        )}
      </div>
      {act.dialog}
    </section>
  );
};

const NotizieList = ({ edit }) => {
  const { items, reload } = useNotizie();
  const act = useNotiziaActions(reload);
  const [filtro, setFiltro] = useState("tutte");
  const shown = (items || []).filter((n) => filtro === "tutte" || n.tipo === filtro);
  return (
    <section className="min-h-[70vh] bg-slate-50 py-16" data-testid="sito-notizie-list">
      <div className="mx-auto max-w-6xl px-5">
        <Link to="/sito" className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-[#166534] hover:underline" data-testid="sito-notizie-back"><ArrowLeft className="h-4 w-4" /> Torna al sito</Link>
        <SectionTitle kicker="Notizie ed eventi" title="Comunicati ed eventi" />
        <div className="-mt-4 mb-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2">
            {[["tutte", "Tutte"], ["comunicato", "Comunicati"], ["evento", "Eventi"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setFiltro(k)} data-testid={`notizie-filtro-${k}`}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${filtro === k ? "bg-[#14382C] text-white" : "border border-slate-300 bg-white text-slate-600 hover:border-[#166534]"}`}>{l}</button>
            ))}
          </div>
          {edit && <NewNotiziaButton onClick={act.openNew} />}
        </div>
        {items === null ? null : shown.length === 0 ? <p className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="notizie-empty">Nessuna notizia da mostrare.</p> : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((n, i) => <NotiziaCard key={n.id} n={n} i={i} edit={edit} onEdit={act.openEdit} onDelete={act.remove} />)}
          </div>
        )}
      </div>
      {act.dialog}
    </section>
  );
};

const NotiziaDetail = ({ edit }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [n, setN] = useState(undefined);
  const load = useCallback(() => { api.get(`/public/notizie/${id}`).then((r) => { setN(r.data); }).catch(() => { setN(null); }); }, [id]);
  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);
  const act = useNotiziaActions(load);
  const remove = async () => { if (await act.remove(n)) { navigate("/sito/notizie"); } };
  if (n === undefined) { return <div className="min-h-[60vh]" />; }
  return (
    <article className="mx-auto min-h-[60vh] max-w-3xl px-5 py-14" data-testid="sito-notizia-detail">
      <RouterLink to="/sito/notizie" className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-[#166534] hover:underline" data-testid="sito-notizia-back"><ArrowLeft className="h-4 w-4" /> Tutte le notizie</RouterLink>
      {n === null ? <p className="text-slate-500" data-testid="notizia-not-found">Notizia non trovata.</p> : (
        <>
          <NotiziaMeta n={n} />
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-[#14382C] sm:text-4xl" data-testid="notizia-detail-title">{n.titolo}</h1>
          {edit && (
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => act.openEdit(n)} className="inline-flex items-center gap-1.5 rounded-full bg-[#D97706] px-3 py-1.5 text-xs font-semibold text-white" data-testid="notizia-detail-edit"><Pencil className="h-3.5 w-3.5" /> Modifica</button>
              <button type="button" onClick={remove} className="inline-flex items-center gap-1.5 rounded-full border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700" data-testid="notizia-detail-delete">Elimina</button>
            </div>
          )}
          {notiziaImg(n) && <img src={notiziaImg(n)} alt={n.titolo} className="mt-8 max-h-[70vh] w-full rounded-xl object-cover" data-testid="notizia-detail-image" />}
          <div className="mt-8 whitespace-pre-line text-base leading-relaxed text-slate-700" data-testid="notizia-detail-text">{n.testo}</div>
        </>
      )}
      {act.dialog}
    </article>
  );
};

const SubPage = ({ testid, children }) => {
  const { c, raw, reload } = useSito();
  const em = useEditMode();
  const { open, dialog } = useEditorState(c, raw, reload);
  return (
    <div className="flex min-h-screen flex-col bg-slate-50" data-testid={testid}>
      <Header />
      <main className="flex-1">{children(em.edit)}</main>
      <Footer d={c.footer} edit={em.edit} onEdit={open("footer")} />
      <EditToggle {...em} />
      {dialog}
    </div>
  );
};

export function SitoNotizie() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return <SubPage testid="sito-notizie-page">{(edit) => <NotizieList edit={edit} />}</SubPage>;
}

export function SitoNotizia() {
  return <SubPage testid="sito-notizia-page">{(edit) => <NotiziaDetail edit={edit} />}</SubPage>;
}

const PaginaPersonalizzata = ({ edit }) => {
  const { slug } = useParams();
  const [p, setP] = useState(undefined);
  const { pagine } = usePagine();
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/public/sito/pagine/${slug}`).then((r) => { setP(r.data); }).catch(() => { setP(null); });
  }, [slug, pagine]);
  if (p === undefined) { return <div className="min-h-[60vh]" />; }
  if (p === null) { return <p className="mx-auto min-h-[60vh] max-w-6xl px-5 py-16 text-slate-500" data-testid="pagina-not-found">Pagina non trovata.</p>; }
  return (
    <div className="min-h-[60vh]">
      <div className="bg-[#14382C] py-14">
        <div className="mx-auto max-w-6xl px-5">
          <Link to="/sito" className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-emerald-100 hover:underline" data-testid="pagina-back"><ArrowLeft className="h-4 w-4" /> Torna al sito</Link>
          <h1 className="font-heading text-4xl font-extrabold text-white sm:text-5xl" data-testid="pagina-title">{p.titolo}</h1>
        </div>
      </div>
      <SezioniPersonalizzate pagina={p.id} edit={edit} emptyText={edit ? "La pagina è vuota: aggiungi la prima sezione qui sotto." : "Contenuti in arrivo."} />
    </div>
  );
};

export function SitoPagina() {
  return <SubPage testid="sito-pagina-page">{(edit) => <PaginaPersonalizzata edit={edit} />}</SubPage>;
}

export function SitoGestione() {
  sessionStorage.setItem("sito-edit", "1");
  return <Navigate to="/sito" replace />;
}

const Info = ({ icon: Icon, t, testid, children }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5">
    <Icon className="h-5 w-5 text-[#D97706]" />
    <p className="mt-3 text-xs font-bold uppercase tracking-wide text-slate-400">{t}</p>
    <div className="mt-1 text-sm text-slate-800">{children}</div>
  </div>
);

const Contatti = ({ d, edit, onEdit }) => (
  <section id="contatti" className="relative mx-auto max-w-6xl px-5 py-20">
    <EditButton section="contatti" edit={edit} onClick={onEdit} />
    <SectionTitle kicker={d.kicker} title={d.title} />
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
      <Info icon={Mail} t="Email" testid="sito-email"><a href={`mailto:${d.email}`} className="break-all hover:underline">{d.email}</a></Info>
      <Info icon={Mail} t="PEC" testid="sito-pec"><a href={`mailto:${d.pec}`} className="break-all hover:underline">{d.pec}</a></Info>
      <Info icon={MapPin} t="Sedi" testid="sito-sedi"><p>Sede Legale e Amministrativa: {d.sede_legale}</p><p className="mt-1">Sede Operativa: {d.sede_operativa}</p></Info>
      <Info icon={Landmark} t="Codice Fiscale" testid="sito-cf">{d.cf}</Info>
    </div>
    <div className="mt-10 flex flex-wrap gap-3">
      <Link to="/convenzioni" data-testid="sito-convenzioni-link" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-[#166534] hover:text-[#166534]"><Handshake className="h-4 w-4" /> Convenzioni CRAL ANGE</Link>
      <Link to="/dona" data-testid="sito-dona-link" className="inline-flex items-center gap-2 rounded-full bg-[#166534] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#14532d]"><Heart className="h-4 w-4" /> Sostienici con una donazione</Link>
    </div>
  </section>
);

const Footer = ({ d, edit, onEdit }) => (
  <footer className="relative bg-[#0E281F] py-8 text-center text-xs text-emerald-100/70">
    <EditButton section="footer" edit={edit} onClick={onEdit} dark />
    <p data-testid="sito-footer-text" className="whitespace-pre-line px-16">{d.text}</p>
  </footer>
);

const useEditorState = (c, raw, reload) => {
  const [section, setSection] = useState(null);
  const open = (s) => () => setSection(s);
  const dialog = section ? (
    <SectionEditor key={section} section={section} data={c[section]} open onClose={() => setSection(null)} onSaved={reload}
      extra={section === "hero" ? <HeroImageControl hasCustom={Boolean(raw.hero_image_v)} onChanged={reload} /> : null} />
  ) : null;
  return { open, dialog };
};

export function SitoGalleria() {
  const { c, raw, reload } = useSito();
  const em = useEditMode();
  const { open, dialog } = useEditorState(c, raw, reload);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-slate-50" data-testid="sito-galleria-page">
      <Header />
      <Galleria d={c.galleria} edit={em.edit} onEdit={open("galleria")} />
      <Footer d={c.footer} edit={em.edit} onEdit={open("footer")} />
      <EditToggle {...em} />
      {dialog}
    </div>
  );
}

export default function SitoUfficiale() {
  const { c, raw, reload } = useSito();
  const em = useEditMode();
  const { open, dialog } = useEditorState(c, raw, reload);
  const [pdf, setPdf] = useState(false);
  const { hash } = useLocation();
  useEffect(() => {
    api.get("/public/presentazione").then((r) => { setPdf(Boolean(r.data?.available)); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (hash) { setTimeout(() => { document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }); }, 50); } else { window.scrollTo(0, 0); }
  }, [hash]);
  return (
    <div className="min-h-screen scroll-smooth bg-slate-50" data-testid="sito-ufficiale-page">
      <Header />
      <Hero d={c.hero} imgV={raw.hero_image_v} edit={em.edit} onEdit={open("hero")} />
      <ChiSiamo d={c.chi} edit={em.edit} onEdit={open("chi")} />
      <Corsi d={c.corsi} pdf={pdf} edit={em.edit} onEdit={open("corsi")} />
      <Modulistica d={c.moduli} email={c.contatti.email} edit={em.edit} onEdit={open("moduli")} />
      <UltimeNotizie edit={em.edit} />
      <SezioniPersonalizzate pagina="home" edit={em.edit} />
      <Contatti d={c.contatti} edit={em.edit} onEdit={open("contatti")} />
      <Footer d={c.footer} edit={em.edit} onEdit={open("footer")} />
      <EditToggle {...em} />
      {dialog}
    </div>
  );
}
